import { createBigDecimal, toStringBigDecimal } from '@0xtorch/big-decimal'
import type { ActionFilter } from '@pkg/basic'
import {
  blankApiKeyEvmChainList,
  blankApiKeyPolkadotChains,
  retiredEvmChains,
} from '@pkg/basic'
import { isHex, toLowerHex } from '@pkg/evm'
import type { SQL, SQLWrapper } from 'drizzle-orm'
import {
  eq,
  getTableColumns,
  gte,
  inArray,
  isNotNull,
  isNull,
  like,
  lte,
  ne,
  notInArray,
  notLike,
  or,
  sql,
} from 'drizzle-orm'
import type { SQLiteTable } from 'drizzle-orm/sqlite-core'
import { sqliteMaxHostParameterCount } from './constants'
import {
  actionAccountRelationTable,
  actionTable,
  actionTransferTable,
} from './schema'

export const conflictUpdateAllExcept = <
  T extends SQLiteTable,
  E extends (keyof T['$inferInsert'])[],
>(
  table: T,
  except: E,
) => {
  const columns = getTableColumns(table)
  const updateColumns = Object.entries(columns).filter(
    ([col]) => !except.includes(col as keyof typeof table.$inferInsert),
  )

  return Object.fromEntries(
    updateColumns.map(([colName, table]) => [
      colName,
      sql.raw(`excluded."${table.name}"`),
    ]),
  ) as Omit<Record<keyof typeof table.$inferInsert, SQL>, E[number]>
}

export const getMaxInsertRowCount = (columnCount: number, isUpsert = false) =>
  Math.floor(sqliteMaxHostParameterCount / columnCount / (isUpsert ? 2 : 1))

/**
 * decimal 文字列を canonical な表現へ正規化する。
 * @param params 正規化対象の文字列を受け取る。
 * @returns 正規化済み文字列、または不正な値なら undefined を返す。
 */
export const normalizeDecimalText = ({
  value,
}: {
  readonly value: string
}): string | undefined => {
  try {
    return toStringBigDecimal(createBigDecimal(value))
  } catch {
    return undefined
  }
}

const decimalSortKeyAlphabet = '/0123456789'
const invertedDecimalSortKeyAlphabet = '9876543210/'
const decimalSortKeyIntegerLengthWidth = 12

/**
 * 正規化済み decimal 文字列を符号・整数部・小数部へ分解する。
 * @param params 正規化済み decimal 文字列を受け取る。
 * @returns 符号情報と整数部・小数部を返す。
 */
const splitNormalizedDecimalText = ({
  value,
}: {
  readonly value: string
}): {
  readonly isNegative: boolean
  readonly integerPart: string
  readonly fractionPart: string
} => {
  const isNegative = value.startsWith('-')
  const unsignedValue = isNegative ? value.slice(1) : value
  const [integerPart, fractionPart = ''] = unsignedValue.split('.')

  return {
    isNegative,
    integerPart,
    fractionPart,
  }
}

/**
 * decimal sort key の文字列を逆順比較用へ反転する。
 * @param params 反転対象の sort key 部分文字列を受け取る。
 * @returns 文字順を反転した文字列を返す。
 */
const invertDecimalSortKeyText = ({
  value,
}: {
  readonly value: string
}): string => {
  let invertedValue = ''
  for (const char of value) {
    const alphabetIndex = decimalSortKeyAlphabet.indexOf(char)
    if (alphabetIndex === -1) {
      throw new Error(`Unsupported decimal sort key char: ${char}`)
    }
    invertedValue += invertedDecimalSortKeyAlphabet[alphabetIndex]
  }
  return invertedValue
}

/**
 * decimal 文字列を辞書順で正確に大小比較できる sort key へ変換する。
 * @param params decimal 文字列を受け取る。
 * @returns 昇順ソートで数値順と一致する sort key を返す。
 */
export const createDecimalSortKey = ({
  value,
}: {
  readonly value: string
}): string => {
  const normalizedValue = normalizeDecimalText({ value })
  if (normalizedValue === undefined) {
    throw new Error(`Invalid decimal text: ${value}`)
  }

  const { isNegative, integerPart, fractionPart } = splitNormalizedDecimalText({
    value: normalizedValue,
  })
  const integerLengthKey = integerPart.length
    .toString()
    .padStart(decimalSortKeyIntegerLengthWidth, '0')
  const absoluteKey = `${integerLengthKey}${integerPart}${fractionPart}/`

  if (!isNegative) {
    return `1${absoluteKey}`
  }

  return `0${invertDecimalSortKeyText({ value: absoluteKey })}`
}

/**
 * 符号を除いた decimal 文字列の SQL 式を返す。
 * @param params 対象の SQL 値を受け取る。
 * @returns 符号を除去した SQL 式を返す。
 */
const createUnsignedDecimalTextSql = ({
  valueSql,
}: {
  readonly valueSql: SQLWrapper
}): SQL<unknown> => {
  return sql`CASE
    WHEN substr(${valueSql}, 1, 1) = '-' THEN substr(${valueSql}, 2)
    ELSE ${valueSql}
  END`
}

/**
 * decimal 文字列の整数部を返す SQL 式を生成する。
 * @param params 対象の SQL 値を受け取る。
 * @returns 整数部の SQL 式を返す。
 */
const createDecimalIntegerPartSql = ({
  valueSql,
}: {
  readonly valueSql: SQLWrapper
}): SQL<unknown> => {
  const unsignedSql = createUnsignedDecimalTextSql({ valueSql })
  return sql`CASE
    WHEN instr(${unsignedSql}, '.') = 0 THEN ${unsignedSql}
    ELSE substr(${unsignedSql}, 1, instr(${unsignedSql}, '.') - 1)
  END`
}

/**
 * decimal 文字列の小数部を返す SQL 式を生成する。
 * @param params 対象の SQL 値を受け取る。
 * @returns 小数部の SQL 式を返す。
 */
const createDecimalFractionPartSql = ({
  valueSql,
}: {
  readonly valueSql: SQLWrapper
}): SQL<unknown> => {
  const unsignedSql = createUnsignedDecimalTextSql({ valueSql })
  return sql`CASE
    WHEN instr(${unsignedSql}, '.') = 0 THEN ''
    ELSE substr(${unsignedSql}, instr(${unsignedSql}, '.') + 1)
  END`
}

/**
 * decimal 文字列の符号を返す SQL 式を生成する。
 * @param params 対象の SQL 値を受け取る。
 * @returns 負数なら -1、それ以外なら 1 を返す SQL 式を返す。
 */
const createDecimalSignSql = ({
  valueSql,
}: {
  readonly valueSql: SQLWrapper
}): SQL<unknown> => {
  return sql`CASE
    WHEN substr(${valueSql}, 1, 1) = '-' THEN -1
    ELSE 1
  END`
}

/**
 * 絶対値同士の decimal 比較結果を返す SQL 式を生成する。
 * @param params 左辺と右辺を受け取る。
 * @returns 左辺が大きければ 1、小さければ -1、等しければ 0 を返す SQL 式を返す。
 */
const createAbsoluteDecimalCompareSql = ({
  leftSql,
  rightSql,
}: {
  readonly leftSql: SQLWrapper
  readonly rightSql: SQLWrapper
}): SQL<unknown> => {
  const leftIntegerSql = createDecimalIntegerPartSql({ valueSql: leftSql })
  const rightIntegerSql = createDecimalIntegerPartSql({ valueSql: rightSql })
  const leftFractionSql = createDecimalFractionPartSql({ valueSql: leftSql })
  const rightFractionSql = createDecimalFractionPartSql({ valueSql: rightSql })

  return sql`CASE
    WHEN length(${leftIntegerSql}) < length(${rightIntegerSql}) THEN -1
    WHEN length(${leftIntegerSql}) > length(${rightIntegerSql}) THEN 1
    WHEN ${leftIntegerSql} < ${rightIntegerSql} THEN -1
    WHEN ${leftIntegerSql} > ${rightIntegerSql} THEN 1
    WHEN ${leftFractionSql} = ${rightFractionSql} THEN 0
    WHEN ${leftFractionSql} = '' THEN -1
    WHEN ${rightFractionSql} = '' THEN 1
    WHEN substr(${leftFractionSql}, 1, length(${rightFractionSql})) = ${rightFractionSql} THEN 1
    WHEN substr(${rightFractionSql}, 1, length(${leftFractionSql})) = ${leftFractionSql} THEN -1
    WHEN ${leftFractionSql} < ${rightFractionSql} THEN -1
    WHEN ${leftFractionSql} > ${rightFractionSql} THEN 1
    ELSE 0
  END`
}

/**
 * decimal 文字列同士の比較結果を返す SQL 式を生成する。
 * @param params 左辺と右辺を受け取る。
 * @returns 左辺が大きければ 1、小さければ -1、等しければ 0 を返す SQL 式を返す。
 */
const createDecimalCompareSql = ({
  leftSql,
  rightSql,
}: {
  readonly leftSql: SQLWrapper
  readonly rightSql: SQLWrapper
}): SQL<unknown> => {
  const leftSignSql = createDecimalSignSql({ valueSql: leftSql })
  const rightSignSql = createDecimalSignSql({ valueSql: rightSql })
  const absoluteCompareSql = createAbsoluteDecimalCompareSql({
    leftSql,
    rightSql,
  })

  return sql`CASE
    WHEN ${leftSignSql} < ${rightSignSql} THEN -1
    WHEN ${leftSignSql} > ${rightSignSql} THEN 1
    WHEN ${leftSignSql} = 1 THEN ${absoluteCompareSql}
    ELSE -1 * (${absoluteCompareSql})
  END`
}

/**
 * action と account の関連に対する条件 SQL を返す。
 * @param params action id 列、account id 群、包含条件かどうかを受け取る。
 * @returns account relation 条件の SQL 句を返す。
 */
const createActionAccountRelationWhere = ({
  actionIdSql,
  accountIdList,
  inArray: isInArray,
}: {
  readonly actionIdSql: SQLWrapper
  readonly accountIdList: readonly number[]
  readonly inArray: boolean
}): SQL<unknown> => {
  const relationActionIdSql = sql.raw('"actionAccountRelation"."actionId"')
  const relationAccountIdSql = sql.raw('"actionAccountRelation"."accountId"')
  const accountCondition =
    accountIdList.length === 1
      ? sql`${relationAccountIdSql} = ${accountIdList[0]}`
      : inArray(relationAccountIdSql, [...accountIdList])
  const existsCondition = sql`EXISTS (
    SELECT 1
    FROM ${actionAccountRelationTable}
    WHERE ${relationActionIdSql} = ${actionIdSql}
      AND ${accountCondition}
  )`

  return isInArray ? existsCondition : sql`NOT ${existsCondition}`
}

/**
 * action.csvId に対する file 条件 SQL を返す。
 * @param params file id 群、包含条件かどうかを受け取る。
 * @returns file 条件の SQL 句を返す。
 */
const createActionFileWhere = ({
  fileIdList,
  inArray: isInArray,
}: {
  readonly fileIdList: readonly number[]
  readonly inArray: boolean
}): SQL<unknown> => {
  if (fileIdList.length === 1) {
    return isInArray
      ? eq(actionTable.csvId, fileIdList[0])
      : sql`(${actionTable.csvId} <> ${fileIdList[0]} OR ${actionTable.csvId} IS NULL)`
  }

  return isInArray
    ? inArray(actionTable.csvId, [...fileIdList])
    : sql`(${notInArray(actionTable.csvId, [...fileIdList])} OR ${actionTable.csvId} IS NULL)`
}

/**
 * actionTransfer.amount に対する amount 条件の EXISTS 句を返す。
 * @param params action id 列、演算子、比較値を受け取る。
 * @returns amount 条件の SQL 句を返す。
 */
export const createActionAmountWhere = ({
  actionIdSql,
  operator,
  value,
}: {
  readonly actionIdSql: SQLWrapper
  readonly operator: Extract<ActionFilter, { type: 'amount' }>['operator']
  readonly value: string
}): SQL<unknown> => {
  const transferActionIdSql = sql.raw('"actionTransfer"."actionId"')
  const transferAmountSql = sql.raw('"actionTransfer"."amount"')

  const compareSql = createDecimalCompareSql({
    leftSql: transferAmountSql,
    rightSql: sql`${value}`,
  })
  const predicate = (() => {
    switch (operator) {
      case 'eq': {
        return sql`${compareSql} = 0`
      }
      case 'gte': {
        return sql`${compareSql} >= 0`
      }
      case 'lte': {
        return sql`${compareSql} <= 0`
      }
      default: {
        throw new Error(`Unknown amount operator: ${operator satisfies never}`)
      }
    }
  })()

  return sql`EXISTS (
    SELECT 1
    FROM ${actionTransferTable}
    WHERE ${transferActionIdSql} = ${actionIdSql}
      AND ${predicate}
  )`
}

/**
 * transfer の増減件数に対する条件 SQL を返す。
 * @param params action id 列、direction、演算子、比較値を受け取る。
 * @returns transfer 件数条件の SQL 句を返す。
 */
export const createActionTransferCountWhere = ({
  actionIdSql,
  direction,
  operator,
  value,
}: {
  readonly actionIdSql: SQLWrapper
  readonly direction: 'in' | 'out'
  readonly operator: Extract<
    ActionFilter,
    { type: 'transfer-in' | 'transfer-out' }
  >['operator']
  readonly value: number
}): SQL<unknown> => {
  const transferActionIdSql = sql.raw('"actionTransfer"."actionId"')
  const transferDirectionSql = sql.raw('"actionTransfer"."direction"')
  const countSql = sql<number>`(
    SELECT COUNT(*)
    FROM ${actionTransferTable}
    WHERE ${transferActionIdSql} = ${actionIdSql}
      AND ${transferDirectionSql} = ${direction}
  )`

  switch (operator) {
    case 'eq': {
      return sql`${countSql} = ${value}`
    }
    case 'gte': {
      return sql`${countSql} >= ${value}`
    }
    case 'lte': {
      return sql`${countSql} <= ${value}`
    }
    default: {
      throw new Error(
        `Unknown transfer count operator: ${operator satisfies never}`,
      )
    }
  }
}

export const createWhereConditionsByActionFilterList = (
  filterList: ActionFilter[],
): SQL<unknown>[] => {
  const mut_whereConditions: SQL<unknown>[] = []
  for (const filter of filterList) {
    switch (filter.type) {
      case 'amount': {
        mut_whereConditions.push(
          createActionAmountWhere({
            actionIdSql: actionTable.id,
            operator: filter.operator,
            value: filter.value,
          }),
        )
        break
      }
      case 'account': {
        if (filter.accountIdList.length > 0) {
          mut_whereConditions.push(
            createActionAccountRelationWhere({
              actionIdSql: actionTable.id,
              accountIdList: filter.accountIdList,
              inArray: filter.inArray,
            }),
          )
        }
        break
      }
      case 'action': {
        const typeConditions: SQL<unknown>[] = []

        if (filter.actionList.length === 1) {
          typeConditions.push(eq(actionTable.type, filter.actionList[0]))
        } else if (filter.actionList.length > 1) {
          typeConditions.push(inArray(actionTable.type, [...filter.actionList]))
        }

        if (filter.subTypeIdList.length === 1) {
          typeConditions.push(eq(actionTable.subType, filter.subTypeIdList[0]))
        } else if (filter.subTypeIdList.length > 1) {
          typeConditions.push(
            inArray(actionTable.subType, [...filter.subTypeIdList]),
          )
        }

        if (typeConditions.length === 0) {
          break
        }

        if (filter.inArray) {
          mut_whereConditions.push(
            typeConditions.length === 1
              ? typeConditions[0]
              : // biome-ignore lint/style/noNonNullAssertion: 条件数 2 以上のみ到達する
                or(...typeConditions)!,
          )
        } else {
          if (filter.actionList.length === 1) {
            mut_whereConditions.push(ne(actionTable.type, filter.actionList[0]))
          } else if (filter.actionList.length > 1) {
            mut_whereConditions.push(
              notInArray(actionTable.type, [...filter.actionList]),
            )
          }
          if (filter.subTypeIdList.length === 1) {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: null も除外対象に含める
              or(
                ne(actionTable.subType, filter.subTypeIdList[0]),
                isNull(actionTable.subType),
              )!,
            )
          } else if (filter.subTypeIdList.length > 1) {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: null も除外対象に含める
              or(
                notInArray(actionTable.subType, [...filter.subTypeIdList]),
                isNull(actionTable.subType),
              )!,
            )
          }
        }
        break
      }
      case 'app': {
        if (filter.appIdList.length === 1) {
          mut_whereConditions.push(
            filter.inArray
              ? eq(actionTable.app, filter.appIdList[0])
              : // biome-ignore lint/style/noNonNullAssertion: <explanation>
                or(
                  ne(actionTable.app, filter.appIdList[0]),
                  isNull(actionTable.app),
                )!,
          )
        } else if (filter.appIdList.length > 1) {
          mut_whereConditions.push(
            filter.inArray
              ? inArray(actionTable.app, [...filter.appIdList])
              : // biome-ignore lint/style/noNonNullAssertion: <explanation>
                or(
                  notInArray(actionTable.app, [...filter.appIdList]),
                  isNull(actionTable.app),
                )!,
          )
        }
        break
      }
      case 'asset': {
        if (filter.assetIdList.length === 1) {
          mut_whereConditions.push(
            filter.inArray
              ? like(actionTable.relatedAssetIds, `%${filter.assetIdList[0]}%`)
              : notLike(
                  actionTable.relatedAssetIds,
                  `%${filter.assetIdList[0]}%`,
                ),
          )
        } else if (filter.assetIdList.length > 1) {
          if (filter.inArray) {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              or(
                ...filter.assetIdList.map((assetId) =>
                  like(actionTable.relatedAssetIds, `%${assetId}%`),
                ),
              )!,
            )
          } else {
            mut_whereConditions.push(
              ...filter.assetIdList.map((assetId) =>
                notLike(actionTable.relatedAssetIds, `%${assetId}%`),
              ),
            )
          }
        }
        break
      }
      case 'category': {
        // category id を source id の prefix に変換
        const prefixList = filter.categoryIdList.map((categoryId) => {
          // EVM Chain category の場合
          const evmChain = [
            ...blankApiKeyEvmChainList,
            ...retiredEvmChains,
          ].find((chain) => chain.network === categoryId)
          if (evmChain !== undefined) {
            return `evm_${evmChain.id}_0x`
          }
          // Polkadot Chain category の場合
          const polkadotChain = blankApiKeyPolkadotChains.find(
            (chain) => chain.network === categoryId,
          )
          if (polkadotChain !== undefined) {
            return `polkadot_${polkadotChain.id}_`
          }

          // それ以外の場合
          return `${categoryId}_`
        })

        if (prefixList.length === 1) {
          mut_whereConditions.push(
            filter.inArray
              ? like(actionTable.sourceId, `${prefixList[0]}%`)
              : notLike(actionTable.sourceId, `${prefixList[0]}%`),
          )
        } else if (prefixList.length > 1) {
          if (filter.inArray) {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              or(
                ...prefixList.map((prefix) =>
                  like(actionTable.sourceId, `${prefix}%`),
                ),
              )!,
            )
          } else {
            mut_whereConditions.push(
              ...prefixList.map((prefix) =>
                notLike(actionTable.sourceId, `${prefix}%`),
              ),
            )
          }
        }
        break
      }
      case 'file': {
        if (filter.fileIdList.length > 0) {
          mut_whereConditions.push(
            createActionFileWhere({
              fileIdList: filter.fileIdList,
              inArray: filter.inArray,
            }),
          )
        }
        break
      }
      case 'comment': {
        switch (filter.operator) {
          case 'eq': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(eq(actionTable.comment, filter.value))
            }
            break
          }
          case 'is-empty': {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              or(isNull(actionTable.comment), eq(actionTable.comment, ''))!,
            )
            break
          }
          case 'is-not-empty': {
            mut_whereConditions.push(isNotNull(actionTable.comment))
            mut_whereConditions.push(ne(actionTable.comment, ''))
            break
          }
          case 'like': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(
                like(actionTable.comment, `%${filter.value}%`),
              )
            }
            break
          }
          case 'not-like': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(isNotNull(actionTable.comment))
              mut_whereConditions.push(ne(actionTable.comment, ''))
              mut_whereConditions.push(
                notLike(actionTable.comment, `%${filter.value}%`),
              )
            }
            break
          }
          default: {
            throw new Error(`Unknown operator: ${filter satisfies never}`)
          }
        }
        break
      }
      case 'cross-id': {
        switch (filter.operator) {
          case 'eq': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(eq(actionTable.crossId, filter.value))
            }
            break
          }
          case 'is-empty': {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              or(isNull(actionTable.crossId), eq(actionTable.crossId, ''))!,
            )
            break
          }
          case 'is-not-empty': {
            mut_whereConditions.push(isNotNull(actionTable.crossId))
            mut_whereConditions.push(ne(actionTable.crossId, ''))
            break
          }
          case 'like': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(
                like(actionTable.crossId, `%${filter.value}%`),
              )
            }
            break
          }
          case 'not-like': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(isNotNull(actionTable.crossId))
              mut_whereConditions.push(ne(actionTable.crossId, ''))
              mut_whereConditions.push(
                notLike(actionTable.crossId, `%${filter.value}%`),
              )
            }
            break
          }
          default: {
            throw new Error(`Unknown operator: ${filter satisfies never}`)
          }
        }
        break
      }
      case 'error': {
        if (filter.errorList.length === 1) {
          switch (filter.errorList[0]) {
            case 'insufficient-amont': {
              mut_whereConditions.push(
                filter.inArray
                  ? eq(actionTable.hasInsufficientAmountTx, true)
                  : ne(actionTable.hasInsufficientAmountTx, true),
              )
              break
            }
            case 'need-check': {
              mut_whereConditions.push(
                filter.inArray
                  ? eq(actionTable.evidence, 'none')
                  : ne(actionTable.evidence, 'none'),
              )
              break
            }
            case 'unknown-price': {
              mut_whereConditions.push(
                filter.inArray
                  ? eq(actionTable.hasUnknownPriceTx, true)
                  : ne(actionTable.hasUnknownPriceTx, true),
              )
              break
            }
            default: {
              throw new Error(
                `Unknown value: ${filter.errorList[0] satisfies never}`,
              )
            }
          }
        } else if (filter.errorList.length > 1) {
          if (filter.inArray) {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              or(
                ...filter.errorList.map((error) => {
                  switch (error) {
                    case 'insufficient-amont': {
                      return eq(actionTable.hasInsufficientAmountTx, true)
                    }
                    case 'need-check': {
                      return eq(actionTable.evidence, 'none')
                    }
                    case 'unknown-price': {
                      return eq(actionTable.hasUnknownPriceTx, true)
                    }
                    default: {
                      throw new Error(`Unknown value: ${error satisfies never}`)
                    }
                  }
                }),
              )!,
            )
          } else {
            mut_whereConditions.push(
              ...filter.errorList.map((error) => {
                switch (error) {
                  case 'insufficient-amont': {
                    return ne(actionTable.hasInsufficientAmountTx, true)
                  }
                  case 'need-check': {
                    return ne(actionTable.evidence, 'none')
                  }
                  case 'unknown-price': {
                    return ne(actionTable.hasUnknownPriceTx, true)
                  }
                  default: {
                    throw new Error(`Unknown value: ${error satisfies never}`)
                  }
                }
              }),
            )
          }
        }
        break
      }
      case 'evidence': {
        if (filter.evidenceList.length === 1) {
          mut_whereConditions.push(
            filter.inArray
              ? eq(actionTable.evidence, filter.evidenceList[0])
              : ne(actionTable.evidence, filter.evidenceList[0]),
          )
        } else if (filter.evidenceList.length > 1) {
          mut_whereConditions.push(
            filter.inArray
              ? inArray(actionTable.evidence, [...filter.evidenceList])
              : notInArray(actionTable.evidence, [...filter.evidenceList]),
          )
        }
        break
      }
      case 'evm-function': {
        switch (filter.operator) {
          case 'eq': {
            const value = filter.value.trim()
            if (value.length > 0) {
              const functionIdHexText = isHex(value)
                ? toLowerHex(value).slice(2)
                : undefined
              const functionCondition =
                functionIdHexText === undefined
                  ? sql`"evmFunctionName" = ${value}`
                  : sql`(
                    "evmFunctionName" = ${value}
                    OR lower(hex("evmFunctionId")) = ${functionIdHexText}
                  )`
              mut_whereConditions.push(sql`${actionTable.sourceId} IN (
                SELECT "id"
                FROM "actionSource"
                WHERE "type" = 'evm'
                  AND ${functionCondition}
              )`)
            }
            break
          }
          case 'is-empty': {
            mut_whereConditions.push(sql`${actionTable.sourceId} IN (
              SELECT "id"
              FROM "actionSource"
              WHERE "type" = 'evm'
                AND ("evmFunctionName" IS NULL OR "evmFunctionName" = '')
            )`)
            break
          }
          case 'is-not-empty': {
            mut_whereConditions.push(sql`${actionTable.sourceId} IN (
              SELECT "id"
              FROM "actionSource"
              WHERE "type" = 'evm'
                AND "evmFunctionName" IS NOT NULL
                AND "evmFunctionName" != ''
            )`)
            break
          }
          case 'like': {
            const value = filter.value.trim()
            if (value.length > 0) {
              const functionIdHexText = isHex(value)
                ? toLowerHex(value).slice(2)
                : undefined
              const functionCondition =
                functionIdHexText === undefined
                  ? sql`"evmFunctionName" LIKE ${`%${value}%`}`
                  : sql`(
                    "evmFunctionName" LIKE ${`%${value}%`}
                    OR lower(hex("evmFunctionId")) LIKE ${`%${functionIdHexText}%`}
                  )`
              mut_whereConditions.push(sql`${actionTable.sourceId} IN (
                SELECT "id"
                FROM "actionSource"
                WHERE "type" = 'evm'
                  AND ${functionCondition}
              )`)
            }
            break
          }
          case 'not-like': {
            const value = filter.value.trim()
            if (value.length > 0) {
              const functionIdHexText = isHex(value)
                ? toLowerHex(value).slice(2)
                : undefined
              const functionCondition =
                functionIdHexText === undefined
                  ? sql`("evmFunctionName" IS NOT NULL AND "evmFunctionName" != '' AND "evmFunctionName" NOT LIKE ${`%${value}%`})`
                  : sql`(
                    (
                      "evmFunctionName" IS NOT NULL
                      AND "evmFunctionName" != ''
                      AND "evmFunctionName" NOT LIKE ${`%${value}%`}
                    )
                    AND (
                      "evmFunctionId" IS NOT NULL
                      AND lower(hex("evmFunctionId")) NOT LIKE ${`%${functionIdHexText}%`}
                    )
                  )`
              mut_whereConditions.push(sql`${actionTable.sourceId} IN (
                SELECT "id"
                FROM "actionSource"
                WHERE "type" = 'evm'
                  AND ${functionCondition}
              )`)
            }
            break
          }
          default: {
            throw new Error(`Unknown operator: ${filter satisfies never}`)
          }
        }
        break
      }
      case 'evm-to': {
        switch (filter.operator) {
          case 'eq': {
            const value = filter.value.trim()
            if (value.length > 0 && isHex(value)) {
              const evmToHexText = toLowerHex(value).slice(2)
              mut_whereConditions.push(sql`${actionTable.sourceId} IN (
                SELECT "id"
                FROM "actionSource"
                WHERE "type" = 'evm'
                  AND ${sql`lower(hex("evmTo")) = ${evmToHexText}`}
              )`)
            }
            break
          }
          case 'is-empty': {
            mut_whereConditions.push(sql`${actionTable.sourceId} IN (
              SELECT "id"
              FROM "actionSource"
              WHERE "type" = 'evm'
                AND "evmTo" IS NULL
            )`)
            break
          }
          case 'is-not-empty': {
            mut_whereConditions.push(sql`${actionTable.sourceId} IN (
              SELECT "id"
              FROM "actionSource"
              WHERE "type" = 'evm'
                AND "evmTo" IS NOT NULL
            )`)
            break
          }
          case 'like': {
            const value = filter.value.trim()
            if (value.length > 0) {
              const normalizedValue = value.startsWith('0x')
                ? value.slice(2)
                : value
              mut_whereConditions.push(sql`${actionTable.sourceId} IN (
                SELECT "id"
                FROM "actionSource"
                WHERE "type" = 'evm'
                  AND lower(hex("evmTo")) LIKE ${`%${normalizedValue.toLowerCase()}%`}
              )`)
            }
            break
          }
          case 'not-like': {
            const value = filter.value.trim()
            if (value.length > 0) {
              const normalizedValue = value.startsWith('0x')
                ? value.slice(2)
                : value
              mut_whereConditions.push(sql`${actionTable.sourceId} IN (
                SELECT "id"
                FROM "actionSource"
                WHERE "type" = 'evm'
                  AND "evmTo" IS NOT NULL
                  AND lower(hex("evmTo")) NOT LIKE ${`%${normalizedValue.toLowerCase()}%`}
              )`)
            }
            break
          }
          default: {
            throw new Error(`Unknown operator: ${filter satisfies never}`)
          }
        }
        break
      }
      case 'loan-id': {
        switch (filter.operator) {
          case 'eq': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(eq(actionTable.loanId, filter.value))
            }
            break
          }
          case 'is-empty': {
            mut_whereConditions.push(
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              or(isNull(actionTable.loanId), eq(actionTable.loanId, ''))!,
            )
            break
          }
          case 'is-not-empty': {
            mut_whereConditions.push(isNotNull(actionTable.loanId))
            mut_whereConditions.push(ne(actionTable.loanId, ''))
            break
          }
          case 'like': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(
                like(actionTable.loanId, `%${filter.value}%`),
              )
            }
            break
          }
          case 'not-like': {
            if (filter.value.length > 0) {
              mut_whereConditions.push(isNotNull(actionTable.loanId))
              mut_whereConditions.push(ne(actionTable.loanId, ''))
              mut_whereConditions.push(
                notLike(actionTable.loanId, `%${filter.value}%`),
              )
            }
            break
          }
          default: {
            throw new Error(`Unknown operator: ${filter satisfies never}`)
          }
        }
        break
      }
      case 'timestamp': {
        if (filter.from !== undefined && filter.to !== undefined) {
          mut_whereConditions.push(
            gte(actionTable.timestamp, new Date(filter.from)),
            lte(actionTable.timestamp, new Date(filter.to)),
          )
        }
        break
      }
      case 'transfer-in': {
        mut_whereConditions.push(
          createActionTransferCountWhere({
            actionIdSql: actionTable.id,
            direction: 'in',
            operator: filter.operator,
            value: filter.value,
          }),
        )
        break
      }
      case 'transfer-out': {
        mut_whereConditions.push(
          createActionTransferCountWhere({
            actionIdSql: actionTable.id,
            direction: 'out',
            operator: filter.operator,
            value: filter.value,
          }),
        )
        break
      }
      case 'transfer': {
        if (filter.addressList.length === 1) {
          switch (filter.operator) {
            case 'in-array': {
              mut_whereConditions.push(
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                or(
                  like(actionTable.fromAddresses, `%${filter.addressList[0]}%`),
                  like(actionTable.toAddresses, `%${filter.addressList[0]}%`),
                )!,
              )
              break
            }
            case 'in-array-from': {
              mut_whereConditions.push(
                like(actionTable.fromAddresses, `%${filter.addressList[0]}%`),
              )
              break
            }
            case 'in-array-to': {
              mut_whereConditions.push(
                like(actionTable.toAddresses, `%${filter.addressList[0]}%`),
              )
              break
            }
            case 'not-in-array': {
              mut_whereConditions.push(
                notLike(
                  actionTable.fromAddresses,
                  `%${filter.addressList[0]}%`,
                ),
                notLike(actionTable.toAddresses, `%${filter.addressList[0]}%`),
              )
              break
            }
            case 'not-in-array-from': {
              mut_whereConditions.push(
                notLike(
                  actionTable.fromAddresses,
                  `%${filter.addressList[0]}%`,
                ),
              )
              break
            }
            case 'not-in-array-to': {
              mut_whereConditions.push(
                notLike(actionTable.toAddresses, `%${filter.addressList[0]}%`),
              )
              break
            }
            default: {
              throw new Error(`Unknown value: ${filter satisfies never}`)
            }
          }
        } else if (filter.addressList.length > 1) {
          switch (filter.operator) {
            case 'in-array': {
              mut_whereConditions.push(
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                or(
                  ...filter.addressList.flatMap((address) => [
                    like(actionTable.fromAddresses, `%${address}%`),
                    like(actionTable.toAddresses, `%${address}%`),
                  ]),
                )!,
              )
              break
            }
            case 'in-array-from': {
              mut_whereConditions.push(
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                or(
                  ...filter.addressList.map((address) =>
                    like(actionTable.fromAddresses, `%${address}%`),
                  ),
                )!,
              )
              break
            }
            case 'in-array-to': {
              mut_whereConditions.push(
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                or(
                  ...filter.addressList.map((address) =>
                    like(actionTable.toAddresses, `%${address}%`),
                  ),
                )!,
              )
              break
            }
            case 'not-in-array': {
              mut_whereConditions.push(
                ...filter.addressList.flatMap((address) => [
                  notLike(actionTable.fromAddresses, `%${address}%`),
                  notLike(actionTable.toAddresses, `%${address}%`),
                ]),
              )
              break
            }
            case 'not-in-array-from': {
              mut_whereConditions.push(
                ...filter.addressList.map((address) =>
                  notLike(actionTable.fromAddresses, `%${address}%`),
                ),
              )
              break
            }
            case 'not-in-array-to': {
              mut_whereConditions.push(
                ...filter.addressList.map((address) =>
                  notLike(actionTable.toAddresses, `%${address}%`),
                ),
              )
              break
            }
            default: {
              throw new Error(`Unknown value: ${filter satisfies never}`)
            }
          }
        }
        break
      }
      default: {
        throw new Error(`Unknown value: ${filter satisfies never}`)
      }
    }
  }
  return mut_whereConditions
}
